import { useEffect, useRef } from "react";
import { gsap, motionOK } from "../hooks/useSmoothScroll";
import { useLang } from "../hooks/useLanguage";
import { Reveal } from "./Reveal";

const Comparison = () => {
  const ref = useRef(null);
  const { t } = useLang();

  useEffect(() => {
    if (!motionOK()) return undefined;
    const ctx = gsap.context(() => {
      gsap.utils.toArray(".cmp-row").forEach((row) => {
        gsap.fromTo(
          row,
          { opacity: 0.12, y: 28 },
          {
            opacity: 1,
            y: 0,
            ease: "none",
            scrollTrigger: { trigger: row, start: "top 96%", end: "top 58%", scrub: true },
          }
        );
      });
    }, ref);
    return () => ctx.revert();
  }, []);

  return (
    <section ref={ref} id="difference" className="bg-[#202020] text-ivory py-24 md:py-32" data-testid="comparison-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <Reveal className="max-w-3xl">
          <p className="label-caps">{t.UI.comparisonLabel}</p>
          <h2 className="mt-4 font-serif-lux font-light text-[clamp(2rem,4.5vw,3.4rem)] leading-[1.08] tracking-tight">
            {t.UI.comparisonTitle}
          </h2>
        </Reveal>

        <div className="mt-14 hidden md:block">
          <div className="grid grid-cols-3 gap-8 border-b border-champagne/40 py-5">
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-ivory/45">
              {t.UI.comparisonAspect}
            </span>
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-ivory/45">
              {t.UI.comparisonStandard}
            </span>
            <span className="text-[0.68rem] font-semibold uppercase tracking-[0.24em] text-champagne">
              {t.UI.comparisonStructured}
            </span>
          </div>
          {t.COMPARISON.map((r, i) => (
            <div
              key={r.row}
              className="cmp-row grid grid-cols-3 gap-8 border-b hairline-dark py-9"
              data-testid={`comparison-row-${i + 1}`}
            >
              <span className="text-[0.72rem] uppercase tracking-[0.2em] text-ivory/55 pt-1">{r.row}</span>
              <p className="text-ivory/40 leading-relaxed">{r.standard}</p>
              <p className="text-ivory leading-relaxed border-s-2 border-champagne ps-6">{r.structured}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 md:hidden space-y-6">
          {t.COMPARISON.map((r, i) => (
            <div key={r.row} className="cmp-row border hairline-dark p-6" data-testid={`comparison-card-${i + 1}`}>
              <p className="label-caps">{r.row}</p>
              <p className="mt-5 text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-ivory/45">
                {t.UI.comparisonStandard}
              </p>
              <p className="mt-2 text-sm text-ivory/40 leading-relaxed">{r.standard}</p>
              <p className="mt-5 text-[0.68rem] font-semibold uppercase tracking-[0.22em] text-champagne">
                {t.UI.comparisonStructured}
              </p>
              <p className="mt-2 text-sm text-ivory leading-relaxed border-s-2 border-champagne ps-4">
                {r.structured}
              </p>
            </div>
          ))}
        </div>

        <Reveal className="mt-12 max-w-3xl">
          <p className="text-sm text-ivory/55 leading-relaxed" data-testid="comparison-note">
            {t.UI.comparisonNote}
          </p>
        </Reveal>
      </div>
    </section>
  );
};

export default Comparison;
